import { Box, Stack, Typography, styled } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IPropertyGroup } from "../../models/propertyGroup";
import TextField from "../input/v2/TextField";
import FieldLabel from "../input/v2/FieldLabel";
import SelectField, { SelectOption } from "../input/v2/SelectField";
import TimeField from "../input/v2/TimeField";

const SectionTitle = styled(Typography)(() => ({
  fontWeight: 800,
  fontSize: 16,
}));

interface RouteInfoSectionProps {
  mode: "create" | "edit";
  routeName: string;
  propertyGroupId: number;
  propertyGroups: IPropertyGroup[];
  startTime: string | null;
  endTime: string | null;
  hasInvalidSchedule: boolean;
  onRouteNameChange: (value: string) => void;
  onPropertyGroupChange: (value: number) => void;
  onStartTimeChange: (value: string | null) => void;
  onEndTimeChange: (value: string | null) => void;
}

const RouteInfoSection = ({
  mode,
  routeName,
  propertyGroupId,
  propertyGroups,
  startTime,
  endTime,
  hasInvalidSchedule,
  onRouteNameChange,
  onPropertyGroupChange,
  onStartTimeChange,
  onEndTimeChange,
}: RouteInfoSectionProps) => {
  const { t } = useTranslation();

  const propertyGroupOptions: SelectOption<number>[] = useMemo(
    () =>
      propertyGroups
        .map((group) => ({ value: group.id, label: group.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [propertyGroups]
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <InfoIcon color="primary" sx={{ fontSize: 22 }} />
        <SectionTitle>{t("table.route.informations")}</SectionTitle>
      </Stack>

      <Stack spacing={1.5}>
        <Box>
          <FieldLabel required>{t("table.route.name")}</FieldLabel>
          <TextField
            fullWidth
            required
            placeholder={t("table.route.namePlaceholder")}
            value={routeName}
            onChange={(e) => onRouteNameChange(e.target.value)}
            maxLength={50}
          />
        </Box>

        <Box>
          <FieldLabel required>{t("table.route.propertyGroup")}</FieldLabel>
          <SelectField
            fullWidth
            value={propertyGroupId}
            disabled={mode === "edit"}
            onChange={(value) => onPropertyGroupChange(Number(value))}
            options={propertyGroupOptions}
            placeholder={t("table.route.selectGroup")}
          />
        </Box>

        <Box>
          <FieldLabel>{t("table.route.schedule")}</FieldLabel>
          <Stack direction="row" spacing={1} alignItems="center">
            <TimeField fullWidth value={startTime} onChange={onStartTimeChange} />
            <Typography sx={{ color: "text.secondary", fontWeight: 500, flexShrink: 0, fontSize: 14 }}>
              -
            </Typography>
            <TimeField fullWidth value={endTime} onChange={onEndTimeChange} />
          </Stack>

          {hasInvalidSchedule && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {t("table.route.invalidSchedule")}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default RouteInfoSection;